import React, { createContext, useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { FormattedMessage } from "react-intl";
import Spinner from 'react-bootstrap/Spinner';

import { getPBLData } from "./api/apiService";
import { PBLDataType } from "./types/api.types";
import MainLayout from "./layouts/MainLayout";
import CONSTANTS from "./utils/constants";

type PblResponseContext = {
  pblResponseData: any;
  setPblResponseData: React.Dispatch<any>;
};

export const UUIDContext = createContext<PBLDataType | null>(null);
export const PBLResponseContext = createContext<PblResponseContext>({
  pblResponseData: null,
  setPblResponseData: () => {}
});

const UUIDContexted: React.FC<{ 
  children: React.ReactNode, 
  pblResponseDataState: PblResponseContext
}> = ({ children, pblResponseDataState }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  
  const [PBLData, setPBLData] = useState<PBLDataType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { pblResponseData, setPblResponseData } = pblResponseDataState;

  useEffect(() => {
    const loadPBLData = async () => {
      setLoading(true);
      try {
        const response = await getPBLData(uuid!);
        const httpStatus = response.status;
        const pblStatus = response.data.status;

        setPblResponseData({ ...response, linkId: uuid })

        if (pblStatus === "PAID") {
          navigate(`/${uuid}/success/`);
        }
        else if (pblStatus === "EXPIRED") {
          setPBLData(response.data)
          navigate(`/${uuid}/error`);
        } else if(pblStatus === "REDIRECTED"){
          navigate(`/${uuid}/status`);
        }

        if (httpStatus === 200) {
          setPBLData(response.data)
        }
        else if (
          httpStatus === 400 ||
          httpStatus === 404 ||
          httpStatus === 500
        ) {
          navigate(`/${uuid}/error/`);
        }

      } catch (err: any) {
        navigate(`/${uuid}/error/`);
      } finally {
        setLoading(false);
      }
    };

    if (uuid) {
      loadPBLData();
    }
  }, [uuid]);

  const pblResponseContextValue = {
    pblResponseData,
    setPblResponseData
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="main-spinner-wrapper">
          <Spinner animation="border" role="status" className="main-spinner mb-3">
                    
            <span className="visually-hidden"><FormattedMessage id={CONSTANTS.LOADING.LOADING_TEXT} defaultMessage="Loading..." /></span>
          </Spinner>
          <div className="spinner-text"><FormattedMessage id={CONSTANTS.LOADING.MESSAGE} defaultMessage="We are preparing a Payment page for you. It may take a few seconds." /></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <UUIDContext.Provider value={PBLData}>
      <PBLResponseContext.Provider value={pblResponseContextValue}>
        {children}
      </PBLResponseContext.Provider>
    </UUIDContext.Provider>
  )
}

export const useUuidPblData = () => {
  const pblData = useContext(UUIDContext);
  if (!pblData) {
    throw new Error("useUuidPblData should be used within UUIDContext");
  }

  return pblData;
}

export default UUIDContexted;